import { useState, useEffect, useMemo } from 'react';
import { fetchArtistFutureEventSchedules } from '@/features/artists/services';
import { computeEventSchedules } from '@/features/events/utils/scheduleUtils';
import type { ComputedEventSchedule } from '@/features/events/types/types';

interface UseArtistSchedulesReturn {
    schedules: ComputedEventSchedule[];
    allSchedules: ComputedEventSchedule[];
    schedulesLoading: boolean;
    showPastSchedules: boolean;
    setShowPastSchedules: (show: boolean) => void;
}

/**
 * Custom hook to fetch and manage artist schedule data
 * @param artistId - The unique identifier for the artist
 * @returns Object containing schedules, loading state, and past schedules toggle
 */
export const useArtistSchedules = (
    artistId: string | undefined
): UseArtistSchedulesReturn => {
    const [allSchedules, setAllSchedules] = useState<ComputedEventSchedule[]>([]);
    const [schedulesLoading, setSchedulesLoading] = useState<boolean>(true);
    const [showPastSchedules, setShowPastSchedules] = useState<boolean>(false);

    // Fetch schedule data from API
    useEffect(() => {
        const fetchScheduleData = async () => {
            if (!artistId) {
                setAllSchedules([]);
                setSchedulesLoading(false);
                return;
            }

            setSchedulesLoading(true);
            try {
                // Fetch all future event schedules for this artist
                const scheduleResponse = await fetchArtistFutureEventSchedules(artistId);

                if (scheduleResponse.data.length > 0) {
                    // Extract event-schedule objects from the response
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const eventSchedules = (scheduleResponse.data as any[]).map((item: any) => item['event-schedule']);

                    // Compute schedules using start_time and duration
                    const computedSchedules = computeEventSchedules(eventSchedules);

                    // Filter out cancelled schedules only (keep past schedules for toggle)
                    const validSchedules = computedSchedules.filter(schedule => {
                        return !schedule.cancelled;
                    });

                    setAllSchedules(validSchedules);
                } else {
                    setAllSchedules([]);
                }
            } catch (error) {
                console.error('Error fetching artist schedule data:', error);
                setAllSchedules([]);
            } finally {
                setSchedulesLoading(false);
            }
        };

        fetchScheduleData();
    }, [artistId]);

    // Filter schedules based on showPastSchedules toggle
    const schedules = useMemo(() => {
        if (showPastSchedules) {
            return allSchedules;
        }

        // Filter out past schedules (based on end time)
        const now = new Date();
        return allSchedules.filter(schedule => {
            const startTime = new Date(schedule.start_time);
            const durationMs = schedule.duration / 1e6;
            const endTime = new Date(startTime.getTime() + durationMs);
            return endTime > now;
        });
    }, [allSchedules, showPastSchedules]);

    return {
        schedules,
        allSchedules,
        schedulesLoading,
        showPastSchedules,
        setShowPastSchedules
    };
};
